import React, { useState, useEffect, useRef } from 'react';
import { Paper, Button, Box, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import UndoIcon from '@mui/icons-material/Undo';
import SaveIcon from '@mui/icons-material/Save';
import { useSelector } from 'react-redux';

/**
 * GlassOverImageSVG - SVG-based drawing implementation
 * Uses SVG paths for vector-based drawing that scales perfectly
 * Better for editing individual paths and exporting
 */
const GlassOverImageSVG = ({ id, properties, value, valueUpdaterCallback, validityUpdaterCallback }) => {
    const { options = {} } = properties;
    const language = useSelector(state => state.App_Settings.language);
    const label = options.label || 'Draw Over Image';
    
    const svgRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [currentPath, setCurrentPath] = useState('');
    const [paths, setPaths] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);
    const [imageDimensions, setImageDimensions] = useState({ width: 800, height: 600 });

    // Sync with parent value
    useEffect(() => {
        if (value && value.paths) {
            setPaths(value.paths);
            if (value.imageData) {
                setSelectedImage(value.imageData);
            }
            if (value.imageDimensions) {
                setImageDimensions(value.imageDimensions);
            }
        }
    }, [value]);

    // Update parent whenever paths or image changes
    useEffect(() => {
        if (selectedImage || paths.length > 0) {
            const drawingData = {
                paths: paths,
                imageData: selectedImage,
                imageDimensions: imageDimensions,
                timestamp: new Date().toISOString(),
            };
            valueUpdaterCallback(drawingData);
            validityUpdaterCallback(paths.length > 0 && selectedImage !== null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paths, selectedImage, imageDimensions]);

    const handleImageUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    setSelectedImage(e.target.result);
                    setImageDimensions({ width: img.width, height: img.height });
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        }
    };

    const getCoordinates = (event) => {
        const svg = svgRef.current;
        const rect = svg.getBoundingClientRect();
        
        let clientX, clientY;
        
        if (event.touches && event.touches.length > 0) {
            clientX = event.touches[0].clientX;
            clientY = event.touches[0].clientY;
        } else {
            clientX = event.clientX;
            clientY = event.clientY;
        }

        // Convert to SVG coordinates
        const x = ((clientX - rect.left) / rect.width) * imageDimensions.width;
        const y = ((clientY - rect.top) / rect.height) * imageDimensions.height;

        return { x, y };
    };

    const startDrawing = (event) => {
        event.preventDefault();
        setIsDrawing(true);
        const coords = getCoordinates(event);
        setCurrentPath(`M ${coords.x} ${coords.y}`);
    };

    const draw = (event) => {
        if (!isDrawing) return;
        event.preventDefault();
        
        const coords = getCoordinates(event);
        setCurrentPath((prevPath) => `${prevPath} L ${coords.x} ${coords.y}`);
    };

    const stopDrawing = () => {
        if (isDrawing && currentPath) {
            const newPathObject = {
                d: currentPath,
                color: options.strokeColor || '#FF0000',
                width: options.strokeWidth || 3,
                timestamp: Date.now(),
            };
            setPaths([...paths, newPathObject]);
            setCurrentPath('');
        }
        setIsDrawing(false);
    };

    const handleUndo = () => {
        if (paths.length > 0) {
            setPaths(paths.slice(0, -1));
        }
    };

    const handleClear = () => {
        setPaths([]);
    };

    const exportDrawing = () => {
        const drawingData = {
            paths: paths,
            imageData: selectedImage,
            imageDimensions: imageDimensions,
            metadata: {
                timestamp: new Date().toISOString(),
                totalPaths: paths.length,
                svgViewBox: `0 0 ${imageDimensions.width} ${imageDimensions.height}`,
            }
        };
        
        // Log to console
        console.log('Drawing Data (JSON - SVG):', JSON.stringify(drawingData, null, 2));
        
        // Create downloadable JSON file
        const jsonString = JSON.stringify(drawingData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `drawing-svg-${Date.now()}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        // Also export as standalone SVG file
        const svgString = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${imageDimensions.width} ${imageDimensions.height}">
    <image href="${selectedImage}" width="${imageDimensions.width}" height="${imageDimensions.height}" />
    ${paths.map(path => `<path d="${path.d}" stroke="${path.color}" stroke-width="${path.width}" fill="none" stroke-linecap="round" stroke-linejoin="round" />`).join('\n    ')}
</svg>`;
        
        const svgBlob = new Blob([svgString], { type: 'image/svg+xml' });
        const svgUrl = URL.createObjectURL(svgBlob);
        const svgLink = document.createElement('a');
        svgLink.href = svgUrl;
        svgLink.download = `drawing-${Date.now()}.svg`;
        document.body.appendChild(svgLink);
        svgLink.click();
        document.body.removeChild(svgLink);
        URL.revokeObjectURL(svgUrl);
        
        console.log('Exported JSON and SVG files successfully');
        
        return drawingData;
    };

    return (
        <Paper elevation={3} sx={{ p: 2, backgroundColor: '#e8f5e9' }}>
            <Typography variant="h6" gutterBottom>
                {label} (SVG Implementation)
            </Typography>

            {!selectedImage ? (
                <Box sx={{ textAlign: 'center', p: 3 }}>
                    <Button variant="contained" component="label" color="success">
                        Upload Image
                        <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handleImageUpload}
                        />
                    </Button>
                </Box>
            ) : (
                <Box>
                    <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            color="success"
                            startIcon={<UndoIcon />}
                            onClick={handleUndo}
                            disabled={paths.length === 0}
                        >
                            Undo
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            color="success"
                            startIcon={<DeleteIcon />}
                            onClick={handleClear}
                            disabled={paths.length === 0}
                        >
                            Clear
                        </Button>
                        <Button
                            variant="contained"
                            size="small"
                            color="success"
                            startIcon={<SaveIcon />}
                            onClick={exportDrawing}
                        >
                            Export JSON
                        </Button>
                    </Box>

                    <Box
                        sx={{
                            position: 'relative',
                            border: '2px solid #4caf50',
                            borderRadius: 1,
                            overflow: 'hidden',
                            maxWidth: '100%',
                            touchAction: 'none',
                        }}
                    >
                        <svg
                            ref={svgRef}
                            viewBox={`0 0 ${imageDimensions.width} ${imageDimensions.height}`}
                            style={{
                                display: 'block',
                                width: '100%',
                                height: 'auto',
                                cursor: 'crosshair',
                            }}
                            onMouseDown={startDrawing}
                            onMouseMove={draw}
                            onMouseUp={stopDrawing}
                            onMouseLeave={stopDrawing}
                            onTouchStart={startDrawing}
                            onTouchMove={draw}
                            onTouchEnd={stopDrawing}
                        >
                            {/* Background image */}
                            <image
                                href={selectedImage}
                                width={imageDimensions.width}
                                height={imageDimensions.height}
                                preserveAspectRatio="xMidYMid meet"
                            />

                            {/* All saved paths */}
                            {paths.map((path, index) => (
                                <path
                                    key={index}
                                    d={path.d}
                                    stroke={path.color}
                                    strokeWidth={path.width}
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            ))}

                            {/* Current drawing path */}
                            {currentPath && (
                                <path
                                    d={currentPath}
                                    stroke={options.strokeColor || '#FF0000'}
                                    strokeWidth={options.strokeWidth || 3}
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            )}
                        </svg>
                    </Box>

                    <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#2e7d32' }}>
                        Paths drawn: {paths.length} | Format: SVG Vector
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

export default GlassOverImageSVG;
